import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Heart, Users, Clock, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Demenzbetreuung() {
  return (
    <>
      <Helmet>
        <title>Demenzbetreuung Itzehoe | Alzheimer Pflege zuhause | Phönix Pflegeteam</title>
        <meta name="description" content="Demenzbetreuung Itzehoe ✓ Spezialisierte Pflege bei Alzheimer & Demenz ✓ Zuhause statt Heim ✓ Entlastung für Angehörige ✓ 50€/h (Pflegekasse übernimmt meist alles)" />
        <link rel="canonical" href="https://pflegeteam-itzehoe.de/demenzbetreuung" />
      </Helmet>

      <main id="main-content">
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
                <Users className="w-4 h-4 text-[#1A52D8]" />
                <span className="text-sm font-medium text-[#1A52D8]">Spezialisierte Betreuung</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Demenzbetreuung Itzehoe - In vertrauter Umgebung bleiben
              </h1>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#1A52D8]/20 mb-8 direct-answer">
                <p className="text-lg text-[#475569] leading-relaxed mb-4">
                  <strong className="text-[#1A52D8]">Demenz ist anders:</strong> Menschen mit Alzheimer oder Demenz brauchen nicht nur Pflege, sondern Struktur, Geduld und vertraute Umgebung. Wir bieten spezialisierte Betreuung, die genau das ermöglicht.
                </p>
                <p className="text-lg text-[#475569] leading-relaxed">
                  <strong>Kosten:</strong> 50€/Stunde. <strong>Pflegekasse übernimmt:</strong> Je nach Pflegegrad 761€ bis 2.200€/Monat - meist reicht das vollständig.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8">
              Warum Zuhause bei Demenz so wichtig ist
            </h2>
            
            <div className="prose prose-lg max-w-none text-[#475569] mb-8">
              <p>
                <strong>Demenz-Patienten verlieren ihr Kurzzeitgedächtnis.</strong> Neue Orte, neue Gesichter, neue Abläufe - das ist Terror für sie.
              </p>
              <p>
                Das eigene Zuhause in Itzehoe kennen sie. Die Küche, das Sofa, der Blick aus dem Fenster. Das gibt Sicherheit. Ein Umzug ins Heim verschlechtert Demenz oft rapide - das sehen wir immer wieder.
              </p>
              <p>
                <strong>Unser Ansatz:</strong> Wir kommen zu Ihnen. Feste Bezugspersonen (nicht jeden Tag jemand anderes!). Gleiche Abläufe. Geduld ohne Ende. So funktioniert Demenzbetreuung, die wirklich hilft.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <h3 className="font-bold text-[#1A52D8] mb-4">Was wir tun:</h3>
                <ul className="space-y-2 text-[#64748b]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Tagesstruktur geben (feste Zeiten, Rituale)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Aktivierung (Spaziergänge, einfache Spiele, Gespräche)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Orientierungshilfen (wo bin ich, welcher Tag ist heute)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Sicherheit (Weglauf-Schutz, Sturzprävention)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Körperpflege mit viel Geduld</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#E8F8F8] rounded-xl p-6">
                <h3 className="font-bold text-[#1A52D8] mb-4">Entlastung für Sie als Angehörige:</h3>
                <p className="text-[#475569] text-sm leading-relaxed mb-3">
                  Demenz-Pflege ist 24/7. Sie als Angehöriger können nicht alles allein schaffen. Burnout bei pflegenden Töchtern und Söhnen ist in Itzehoe leider Alltag.
                </p>
                <p className="text-[#475569] text-sm leading-relaxed">
                  Wir kommen 1-2× täglich, übernehmen die anstrengendsten Teile (Körperpflege, Konflikte, Weglaufversuche) und geben Ihnen Luft zum Atmen. Sie bleiben Tochter/Sohn - nicht Vollzeit-Pfleger.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              Demenz in Itzehoe - Unsere Erfahrung
            </h2>
            <div className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
              <p className="text-[#475569] leading-relaxed mb-4">
                Im Kreis Steinburg betreuen wir aktuell über 25 Demenz-Patienten. Vom frühen Stadium (leichte Vergesslichkeit) bis zum Spätstadium (vollständige Orientierungslosigkeit). Jeder Fall ist anders.
              </p>
              <p className="text-[#475569] leading-relaxed">
                Was alle gemeinsam haben: Der Wunsch, zuhause zu bleiben. In Itzehoe, wo man sein ganzes Leben verbracht hat. Wir machen das möglich - oft bis zum Lebensende.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white">
          <div className="container-custom max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ist bei Ihnen Demenz diagnostiziert?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Lassen Sie uns darüber sprechen. Kostenlos, vertraulich, ohne Druck. Wir finden gemeinsam heraus, wie wir helfen können.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt Anrufen: 0151 46173685
              </Button>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
